import React, { useState } from 'react'
import { ColumnDefinition } from 'common/components/DataTable'
import BigSpinner from 'common/components/BigSpinner'
import { DataTable } from 'common/components/DataTable'
import { usePaginatedFetch } from 'common/hooks/usePaginatedFetch'
import Utils from 'common/Utils'
import { Button } from 'react-bootstrap'
import API from 'common/logic/API'
import OperationWorkflowModal from 'common/components/OperationWorkflowModal'
import EditExpectedMovementWorkflow from 'application/budget/plan/EditExpectedMovementWorkflow'
import * as Icon from 'react-bootstrap-icons'
import DaysInterval from 'application/budget/DaysInterval'
import { BudgetPlan, ExpectedMovement } from 'types'

interface ListProps {
    currentPlan: BudgetPlan
}

function ExpectedMovementsList({ currentPlan }: ListProps) {
    const [refreshKey, setRefreshKey] = useState(0)
    const { results, error } = usePaginatedFetch<ExpectedMovementWithExtra>(
        '/expected-movements',
        { month: currentPlan.validFrom, limit: 1000 },
        refreshKey
    )

    function refreshData() { setRefreshKey(k => k + 1) }

    if (error) return <p>Error!</p>
    if (results === null) return <BigSpinner />

    return (
        <ExpectedMovementsTable
            currentPlan={currentPlan}
            results={results}
            refreshCallback={refreshData}
        />
    )
}

interface ExpectedMovementWithExtra extends ExpectedMovement {
    monthlyAmount: string | number
    repeatsAfterDays: number
    currency: { name: string; id: number }
    granted: boolean
    summary: string
    budgetPlan: { id: number }
}

interface TableProps {
    currentPlan: BudgetPlan
    results: ExpectedMovementWithExtra[]
    refreshCallback: () => void
}

export function ExpectedMovementsTable({ currentPlan, results, refreshCallback }: TableProps) {
    const [modal, setModal] = useState<{ type: string; obj?: ExpectedMovementWithExtra } | null>(null)

    const columnDefinitions = [
        new ColumnDefinition<ExpectedMovementWithExtra>("Entry", "summary"),
        new ColumnDefinition<ExpectedMovementWithExtra>("Amount", obj => <>{Utils.formatMoney(obj.amount, true)} {obj.currency.name}</>, "td-currency", "td-right"),
        new ColumnDefinition<ExpectedMovementWithExtra>("How often", obj => new DaysInterval(obj.repeatsAfterDays).inText, "td-size-s"),
        new ColumnDefinition<ExpectedMovementWithExtra>("Monthly amount", obj => <>{Utils.formatMoney(obj.monthlyAmount, true)} {obj.currency.name}</>, "td-currency", "td-right"),
        new ColumnDefinition<ExpectedMovementWithExtra>("Tags", obj => obj.tags?.map(t => t.name).join(', ') || ''),
        new ColumnDefinition<ExpectedMovementWithExtra>("Guaranteed expense", obj => obj.granted ? "Yes" : "No", "td-size-s"),
        new ColumnDefinition<ExpectedMovementWithExtra>("Actions", obj => <>
            <Button variant="link" title="Edit" onClick={() => setModal({ type: "edit", obj })}><Icon.Pencil /></Button> |
            <Button variant="link" title="Delete" onClick={() => setModal({ type: "delete", obj })}><Icon.Trash /></Button>
        </>)
    ]

    let modalEl: React.ReactNode = null
    if (modal) {
        const completion = (result: unknown) => { setModal(null); if (result) refreshCallback() }
        switch (modal.type) {
            case "delete":
                modalEl = <OperationWorkflowModal
                    confirmationStep={() => `Delete expected movement "${modal.obj!.summary}"?`}
                    executionStep={() => API.delete(`/expected-movements/${modal.obj!.id}`)}
                    completionCallback={completion}
                />
                break
            case "create":
                modalEl = <EditExpectedMovementWorkflow
                    currentPlanId={currentPlan.id}
                    onSuccess={completion}
                />
                break
            case "edit":
                modalEl = <EditExpectedMovementWorkflow
                    currentPlanId={modal.obj!.budgetPlan.id}
                    onSuccess={completion}
                    entryId={modal.obj!.id}
                />
                break
            default:
        }
    }

    return (
        <>
            <Button variant="link" onClick={() => setModal({ type: "create" })}>Add new</Button>
            {modalEl}
            <DataTable columns={columnDefinitions} rows={results} />
        </>
    )
}

export default ExpectedMovementsList
